import type { ResultOf, VariablesOf } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import type { CombinedError, UseQueryExecute } from "urql"
import { useBlurAuthToken } from "../../../../blur/useBlurAuth"
import { ActionTimelineFragment } from "../../../../components/ActionTimeline/ActionTimeline.fragment"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../constants"
import { useAccount } from "../../../../polychain/hooks/useAccount"
import type { Address } from "../../../../types"
import { useSetBlockchainActionErrors } from "../../../hooks/useSetBlockchainActionErrors"
import { useSetBlockchainActionErrorsFragment } from "../../../hooks/useSetBlockchainActionErrors.fragment"

const CollectionSweepQuery = graphql(
  `
    query CollectionSweepQuery(
      $paymentAsset: ContractIdentifierInput!
      $collectionSlug: String!
      $maxAmountOfItems: Int!
      $maxPricePerItem: BigDecimal!
      $buyer: Address!
      $recipient: Address
      $blurAuthToken: String
    ) {
      sweepCollection(
        buyer: $buyer
        collectionSlug: $collectionSlug
        maxAmountOfItems: $maxAmountOfItems
        maxPricePerItem: $maxPricePerItem
        paymentAsset: $paymentAsset
        recipient: $recipient
        blurAuthToken: $blurAuthToken
      ) {
        actions {
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `,
  [ActionTimelineFragment, useSetBlockchainActionErrorsFragment],
)

type UseCollectionSweepQueryProps = Omit<
  VariablesOf<typeof CollectionSweepQuery>,
  "buyer" | "blurAuthToken"
> & {
  address?: string
  pause?: boolean
}

type UseCollectionSweepQuery = {
  actions: ResultOf<typeof CollectionSweepQuery>["sweepCollection"]["actions"]
  error: CombinedError | undefined
  fetching: boolean
  refetch: UseQueryExecute
}

export const useCollectionSweepQuery = (
  params: UseCollectionSweepQueryProps,
): UseCollectionSweepQuery => {
  const {
    paymentAsset,
    collectionSlug,
    maxAmountOfItems,
    maxPricePerItem,
    recipient,
    pause,
  } = params
  const { address } = useAccount()
  const blurAuthToken = useBlurAuthToken()
  const [{ data, error, fetching }, refetch] = useQuery({
    query: CollectionSweepQuery,
    variables: {
      paymentAsset,
      collectionSlug,
      maxAmountOfItems,
      maxPricePerItem,
      buyer: address as NonNullable<Address>,
      recipient,
      blurAuthToken,
    },
    pause: !address || pause,
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })
  const actions = data?.sweepCollection.actions ?? []

  useSetBlockchainActionErrors(data?.sweepCollection.errors)

  return {
    actions,
    error,
    fetching,
    refetch: () => refetch({ requestPolicy: "network-only" }),
  }
}
